import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import AdSense from './components/AdSense';
import Map from './components/Map';
import TabbedNavigation from './components/TabbedNavigation';
import ContentSection from './components/ContentSection';

interface IpResponse {
  ip: string;
  ipv4?: string;
  ipv6?: string;
  timestamp: string;
  timezone: string;
  location?: {
    country: string;
    region: string;
    city: string;
    latitude?: string;
    longitude?: string;
  };
}

// API URL Configuration
// Priority: 1. Runtime config.js, 2. Environment variable, 3. Relative path (default)
const getApiUrl = (): string => {
  // Check if runtime config exists (set via config.js)
  if ((window as any).APP_CONFIG?.API_URL) {
    return (window as any).APP_CONFIG.API_URL;
  }
  
  // Check environment variable (set at build time)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Default: use relative path (proxied by nginx)
  return '';
};

function App() {
  const [ipData, setIpData] = useState<IpResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('vpn');

  const API_URL = getApiUrl();

  const fetchIp = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiEndpoint = API_URL ? `${API_URL}/api/ip` : '/api/ip';
      const response = await fetch(apiEndpoint);
      if (!response.ok) {
        throw new Error('Failed to fetch IP address');
      }
      const data: IpResponse = await response.json();
      setIpData(data);
    } catch (err) {
      setError('Error fetching IP address');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchIp();
  }, [fetchIp]);

  // Listen for section changes from obfuscated JS
  useEffect(() => {
    const handleSectionChange = (event: CustomEvent) => {
      setActiveTab(event.detail.activeSection);
    };

    window.addEventListener('sectionChange', handleSectionChange as EventListener);
    return () => window.removeEventListener('sectionChange', handleSectionChange as EventListener);
  }, []);

  const formatTimestamp = (timestamp: string, timezone: string) => {
    const date = new Date(timestamp);
    
    // Try to format the date in the user's detected timezone
    try {
      // Use Intl.DateTimeFormat to format in the detected timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      
      const formattedDate = formatter.format(date);
      
      // Get timezone offset for display
      const timeZoneOffset = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || timezone;
      
      return `${formattedDate} (${timeZoneOffset})`;
    } catch (error) {
      // Fallback to local timezone if timezone detection fails
      console.warn('Failed to format timezone:', timezone, error);
      return `${date.toLocaleString()} (${timezone})`;
    }
  };

  const formatLocation = (location: IpResponse['location']) => {
    if (!location) return null;
    
    const parts = [];
    if (location.city && location.city !== 'Unknown') parts.push(location.city);
    if (location.region && location.region !== 'Unknown') parts.push(location.region);
    if (location.country && location.country !== 'Unknown') parts.push(location.country);
    
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const copyToClipboard = async (ipToCopy?: string) => {
    const ip = ipToCopy || ipData?.ip;
    if (ip && ip !== 'Unknown') {
      try {
        await navigator.clipboard.writeText(ip);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy IP address:', err);
      }
    }
  };

  const usefulLinksTabs = [
    { id: 'vpn', label: 'VPN Services', href: 'https://nordvpn.com', target: '_blank' },
    { id: 'domain', label: 'Domain Registration', href: 'https://namecheap.com', target: '_blank' },
    { id: 'hosting', label: 'Cloud Hosting', href: 'https://digitalocean.com', target: '_blank' },
    { id: 'security', label: 'Security Tools', href: 'https://cloudflare.com', target: '_blank' },
  ];

  const usefulLinksContent = {
    vpn: {
      title: 'VPN Services',
      description: 'Protect your online privacy and security with a reliable VPN service. Browse anonymously, access geo-restricted content, and secure your internet connection.',
      features: [
        'Encrypt your internet connection',
        'Access geo-restricted content',
        'Protect your privacy online',
        'Secure public Wi-Fi connections'
      ],
      ctaText: 'Get VPN Protection with NordVPN',
      ctaHref: 'https://nordvpn.com',
      ctaTarget: '_blank'
    },
    domain: {
      title: 'Domain Registration',
      description: 'Register your perfect domain name and establish your online presence. Get reliable domain registration with competitive pricing and excellent support.',
      features: [
        'Competitive domain pricing',
        'Easy domain management',
        'Free WHOIS privacy protection',
        '24/7 customer support'
      ],
      ctaText: 'Register Your Domain with Namecheap',
      ctaHref: 'https://namecheap.com',
      ctaTarget: '_blank'
    },
    hosting: {
      title: 'Cloud Hosting',
      description: 'Deploy and scale your applications with powerful cloud infrastructure. Get reliable hosting with developer-friendly tools and global data centers.',
      features: [
        'Scalable cloud infrastructure',
        'Developer-friendly tools',
        'Global data centers',
        '99.99% uptime SLA'
      ],
      ctaText: 'Start Hosting with DigitalOcean',
      ctaHref: 'https://digitalocean.com',
      ctaTarget: '_blank'
    },
    security: {
      title: 'Security Tools',
      description: 'Protect your website and applications with enterprise-grade security solutions. Get DDoS protection, SSL certificates, and advanced security features.',
      features: [
        'DDoS protection',
        'SSL certificates',
        'Web application firewall',
        'Advanced security analytics'
      ],
      ctaText: 'Secure Your Site with Cloudflare',
      ctaHref: 'https://cloudflare.com',
      ctaTarget: '_blank'
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    // Use obfuscated scroll function
    if ((window as any).GetMyIPUtils) {
      (window as any).GetMyIPUtils.scrollToSection(tabId);
    }
  };

  return (
    <div className="App">
      <header className="header">
        <h1>Get My IP</h1>
        <p>Discover your public IP address instantly</p>
      </header>
      
      {/* Tabbed Navigation */}
      <TabbedNavigation 
        tabs={usefulLinksTabs}
        activeTab={activeTab}
        onTabClick={handleTabClick}
      />
      
      <div className="container">

        <main className="main">
          <div className="ip-display">
            <div className="ip-label">Your IP Address:</div>
            
            {/* Check if both IPv4 and IPv6 are available */}
            {ipData && ipData.ipv4 && ipData.ipv6 && ipData.ipv6 !== 'Unknown' ? (
              <div className="dual-ip-display">
                <div className="ip-entry">
                  <div className="ip-type">IPv4:</div>
                  <div className="ip-value">{ipData.ipv4}</div>
                  <button
                    className="copy-btn-small"
                    onClick={() => copyToClipboard(ipData.ipv4)}
                    disabled={!ipData.ipv4 || ipData.ipv4 === 'Unknown' || error !== null}
                  >
                    {copied ? '✓' : '⧉'}
                  </button>
                </div>
                <div className="ip-entry">
                  <div className="ip-type">IPv6:</div>
                  <div className="ip-value">{ipData.ipv6}</div>
                  <button
                    className="copy-btn-small"
                    onClick={() => copyToClipboard(ipData.ipv6)}
                    disabled={!ipData.ipv6 || ipData.ipv6 === 'Unknown' || error !== null}
                  >
                    {copied ? '✓' : '⧉'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="ip-value">
                {loading ? 'Loading...' : error ? 'Error' : ipData?.ip || 'Unknown'}
              </div>
            )}
            
            {ipData && (
              <div className="timestamp">
                Last updated: {formatTimestamp(ipData.timestamp, ipData.timezone)}
              </div>
            )}
            
            {ipData && ipData.location && formatLocation(ipData.location) && (
              <div className="location-display">
                <div className="location-label">Location:</div>
                <div className="location-value">{formatLocation(ipData.location)}</div>
              </div>
            )}
            
            {/* Map Component */}
            {ipData && ipData.location && ipData.location.latitude && ipData.location.longitude && (
              <Map 
                latitude={ipData.location.latitude}
                longitude={ipData.location.longitude}
                location={ipData.location}
              />
            )}
            
            {ipData && ipData.timezone && ipData.timezone !== 'UTC' && (
              <div className="timezone-display">
                <div className="timezone-label">Local Time:</div>
                <div className="timezone-value">
                  {(() => {
                    try {
                      const date = new Date(ipData.timestamp);
                      const formatter = new Intl.DateTimeFormat('en-US', {
                        timeZone: ipData.timezone,
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true,
                        timeZoneName: 'short'
                      });
                      return formatter.format(date);
                    } catch (error) {
                      return `${ipData.timezone}`;
                    }
                  })()}
                </div>
              </div>
            )}
            <div className="button-group">
              <button className="refresh-btn" onClick={fetchIp} disabled={loading}>
                {loading ? 'Loading...' : '↻ Refresh'}
              </button>
              {!(ipData && ipData.ipv4 && ipData.ipv6 && ipData.ipv6 !== 'Unknown') && (
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard()}
                  disabled={!ipData?.ip || ipData.ip === 'Unknown' || error !== null}
                >
                  {copied ? '✓ Copied!' : '⧉ Copy IP'}
                </button>
              )}
            </div>
          </div>

          <div className="ad-space">
            <AdSense 
              adSlot="1234567890" // Replace with your actual ad slot ID
              adFormat="rectangle"
              className="responsive-ad"
            />
          </div>

          {/* Content Sections */}
          {Object.entries(usefulLinksContent).map(([key, content]) => (
            <ContentSection
              key={key}
              id={key}
              title={content.title}
              description={content.description}
              features={content.features}
              ctaText={content.ctaText}
              ctaHref={content.ctaHref}
              ctaTarget={content.ctaTarget}
              isActive={activeTab === key}
            />
          ))}

          <div className="ad-space">
            <AdSense 
              adSlot="0987654321" // Replace with your actual ad slot ID
              adFormat="horizontal"
              className="banner-ad"
            />
          </div>
        </main>

        <footer className="footer">
          <p>&copy; 2024 Get My IP. Simple IP lookup service.</p>
        </footer>
      </div>
    </div>
  );
}

export default App;